// The "All Off" / "All On" bars that cap the touch-screen stack.
//
// Flutter clips each bar with a quadratic-bezier arc (fourTouchFunctions.dart
// TopSemiCircleClipper / BottomSemiCircleClipper): the top bar bulges upward,
// the bottom bar bulges downward. CSS can't express that shape, so the same two
// paths are reproduced with an inline SVG clip-path in objectBoundingBox units —
// `Q 0.5,0 1,1` for the top bar and `Q 0.5,1 1,0` for the bottom one, which are
// the Dart curves verbatim.
//
// The gradient direction and text colours are the Dart's too: red #F72119 on the
// "All Off" bar with a white glow, green #66FF00 on "All On" with a black one.

import React from 'react';
import { Box, ButtonBase, Typography, useTheme } from '@mui/material';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';

export default function AllBar({
  variant, // 'off' (top) | 'on' (bottom)
  onClick,
  width,
  height,
  fontSize,
  padding,
}) {
  const theme = useTheme();
  const c = theme.sli;
  const isOff = variant === 'off';

  const clipId = isOff ? 'sli-bar-top' : 'sli-bar-bottom';
  const path = isOff ? 'M0,1 Q0.5,0 1,1 L0,1 Z' : 'M0,0 Q0.5,1 1,0 L0,0 Z';

  return (
    <>
      <Box component="svg" width={0} height={0} sx={{ position: 'absolute' }}>
        <defs>
          <clipPath id={clipId} clipPathUnits="objectBoundingBox">
            <path d={path} />
          </clipPath>
        </defs>
      </Box>

      <ButtonBase
        onClick={onClick}
        sx={{
          width,
          height,
          clipPath: `url(#${clipId})`,
          background: isOff
            ? `linear-gradient(to left, ${c.button}, ${c.text})`
            : `linear-gradient(to right, ${c.button}, ${c.text})`,
          boxShadow: isOff ? '4px 4px 8px 1px rgba(0,0,0,0.2)' : 'none',
          display: 'flex',
          alignItems: isOff ? 'flex-end' : 'flex-start',
          justifyContent: 'center',
        }}
      >
        <Typography
          sx={{
            p: `${padding}px`,
            fontSize,
            fontWeight: 700,
            color: isOff ? '#F72119' : '#66FF00',
            textShadow: isOff
              ? '2px 2px 4px rgba(255,255,255,0.6)'
              : '2px 2px 4px rgba(0,0,0,0.7)',
          }}
        >
          {isOff ? 'All Off' : 'All On'}
        </Typography>
      </ButtonBase>
    </>
  );
}

// ── The desktop card's version: ONE row of two buttons ───────────────────────
//
// The arcs above are drawn for a phone screen, where each bar is ~50px tall. A
// desktop room card is ~180px wide and gave them 24px: the curve ate the label
// ("All Off" clipped into the arc) and two slivers cost a row each. Here the
// pair sit side by side under the switches, in the RelayTile's own look — the
// same raised ButtonColor → FontColor gradient and 20px-family radius, with the
// power icon red for Off and green for On — so they read as part of the panel.
export function AllButtons({ onAll, height = 30, fontSize = 12 }) {
  const theme = useTheme();
  const c = theme.sli;

  const pill = (on) => (
    <ButtonBase
      key={on ? 'on' : 'off'}
      onClick={() => onAll(on)}
      aria-label={on ? 'All On' : 'All Off'}
      sx={{
        flex: 1,
        minWidth: 0,
        height,
        gap: 0.5,
        borderRadius: `${height / 2}px`,
        background: `linear-gradient(to bottom, ${c.button}, ${c.text})`,
        boxShadow: '0 2px 5px rgba(0,0,0,0.22)',
        transition: 'transform 120ms, box-shadow 120ms',
        '&:hover': { boxShadow: `0 0 10px 2px ${on ? '#66FF0055' : '#F4433655'}` },
        '&:active': { transform: 'scale(0.97)' },
      }}
    >
      <PowerSettingsNewIcon sx={{ fontSize: fontSize + 4, color: on ? '#66FF00' : '#F44336' }} />
      <Typography noWrap sx={{ fontSize, fontWeight: 700, color: '#000' }}>
        {on ? 'All On' : 'All Off'}
      </Typography>
    </ButtonBase>
  );

  return (
    <Box sx={{ display: 'flex', gap: 1 }}>
      {pill(false)}
      {pill(true)}
    </Box>
  );
}
